import { BigNumber as BigNumber$1 } from '@ethersproject/bignumber';
import { parseUnits } from '@ethersproject/units';
import { BigNumber } from 'ethers';
import { keccak256, pack } from '@ethersproject/solidity';
import { defaultAbiCoder } from '@ethersproject/abi';

var calculateTotalSupplyOfPicks = function calculateTotalSupplyOfPicks(bitRange, cardinality) {
  return Math.pow(Math.pow(2, BigNumber.from(bitRange).toNumber()), cardinality);
};

var debug = /*#__PURE__*/require('debug')('pt:v4-utils-js:calculateCardinality');
/**
 *
 * @param bitRangeSize The size of the bit range for the draw
 * @param totalSupply Total Supply of the Prize Pool Network
 * @param decimals The number of decimals used to shift totalSupply. If no decimal shifting is needed ignore, default is 0.
 * @returns
 */


function calculateCardinality(bitRangeSize, totalSupply, decimals) {
  if (decimals === void 0) {
    decimals = 0;
  }

  var _totalSupply = BigNumber$1.from(totalSupply);

  var cardinality = 0;
  var numberOfPicks;

  do {
    cardinality++;
    numberOfPicks = parseUnits(calculateTotalSupplyOfPicks(bitRangeSize, cardinality + 1).toString(), decimals);
  } while (numberOfPicks.lt(_totalSupply));

  debug('bitRangeSize: ', BigNumber$1.from(bitRangeSize).toString());
  debug('totalSupply: ', BigNumber$1.from(totalSupply).toString());
  debug('decimals: ', BigNumber$1.from(decimals).toString());
  debug('numberOfPicksCardinalityPlusOne: ', numberOfPicks.toString());
  debug('cardinality: ', cardinality.toString());
  return cardinality;
}

function calculateFractionOfPrize(tierTotalPrizes, tierValue) {
  var value = tierValue ? tierValue.toString() : '0';
  var totalPrizes = tierTotalPrizes || '1';
  var valueAtTierIndexUnformatted = parseUnits(value, 9);
  return valueAtTierIndexUnformatted.div(totalPrizes);
}

function calculateNormalizedBalance(balance, ticketTotalSupply) {
  return balance.div(ticketTotalSupply);
}

var debug$1 = /*#__PURE__*/require('debug')('pt:v4-utils-js:calculatePicksFromAverageTotalSuppliesBetween');

var RATE_NORMALIZATION = 1e9;
/**
 * Splits picks based on the configued DPR.
 * @param bitRangeSize
 * @param dpr normalized with 1e9
 * @param minPickCost
 * @param prizeValue
 * @param totalSupply
 * @param decimals (optional) unit digits to parse totalSupply with
 * @returns
 */

function calculatePrizePoolPicksWithDpr(bitRangeSize, dpr, minPickCost, prizeValue, totalSupply, decimals) {
  if (decimals === void 0) {
    decimals = 0;
  }

  var _dpr = BigNumber$1.from(dpr);

  var _totalSupply = BigNumber$1.from(totalSupply);

  var _prizeValue = BigNumber$1.from(prizeValue);

  var _minPickCost = BigNumber$1.from(minPickCost);

  var targetPicks;

  var odds = _dpr.mul(_totalSupply).div(_prizeValue);

  if (odds.isZero()) {
    return 0;
  }

  if (_prizeValue.gt('0')) {
    targetPicks = _prizeValue.mul(RATE_NORMALIZATION).div(_dpr.mul(_minPickCost));
  } else {
    targetPicks = BigNumber$1.from('0');
  }

  debug$1("targetPicks " + Math.floor(targetPicks.toNumber()));
  var cardinality = calculateCardinality(bitRangeSize, targetPicks, decimals);
  debug$1("targetPicks " + targetPicks);
  debug$1("cardinality " + Math.floor(cardinality));
  var totalNumberOfPicks = BigNumber$1.from(calculateTotalSupplyOfPicks(bitRangeSize, cardinality));
  var prizePoolNumberOfPicks = totalNumberOfPicks.mul(odds).div(RATE_NORMALIZATION);
  debug$1("prizePoolNumberOfPicks " + Math.floor(prizePoolNumberOfPicks.toNumber()));
  return Math.floor(prizePoolNumberOfPicks.toNumber());
}

function calculateNormalizeBalancePicksFromTotalPicks(numberOfPicks, normalizedBalance) {
  if (!numberOfPicks || !normalizedBalance) return BigNumber$1.from(0);
  return BigNumber$1.from(numberOfPicks).mul(BigNumber$1.from(normalizedBalance)).div(BigNumber$1.from('1000000000000000000'));
}

/**
 * Used to calculate the normalized user balances from the total supply of a Prize Pool.
 * @param userBalances
 * @param totalSupply
 * @returns
 */
function calculateNormalizedUserBalancesFromTotalSupply(userBalances, totalSupply) {
  return userBalances.map(function (userBalance) {
    return {
      address: userBalance.address,
      normalizedBalance: userBalance.balance.mul('1000000000000000000').div(totalSupply)
    };
  });
}

function createDrawResultsObject(drawId) {
  return {
    prizes: [],
    totalValue: BigNumber$1.from('0'),
    drawId: drawId
  };
}

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _createForOfIteratorHelperLoose(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
  if (it) return (it = it.call(o)).next.bind(it);

  if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
    if (it) o = it;
    var i = 0;
    return function () {
      if (i >= o.length) return {
        done: true
      };
      return {
        done: false,
        value: o[i++]
      };
    };
  }

  throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var sortByPrizeAwardableDesc = function sortByPrizeAwardableDesc(a, b) {
  var bSubA = b.amount.sub(a.amount);
  if (bSubA.isZero()) return 0;
  if (bSubA.isNegative()) return -1;
  return 1;
};

var debug$2 = /*#__PURE__*/require('debug')('pt:v4-utils-js');

function filterResultsByValue(drawResults, maxPicksPerUser) {
  // if the user has more winning picks than max pick per user for the draw, we sort by value and remove the lowest value picks
  if (drawResults.prizes.length > maxPicksPerUser) {
    debug$2("user has more claims (" + drawResults.prizes.length + ") than the max picks per user (" + maxPicksPerUser + "). Sorting.."); // sort by value

    var descendingSortedPrizes = drawResults.prizes.filter(function (prizeAwardable) {
      return !prizeAwardable.amount.isZero();
    }).sort(sortByPrizeAwardableDesc); // remove the lowest value picks up to the max picks per user

    var slicedDescendingSortedPrizes = descendingSortedPrizes.slice(0, maxPicksPerUser); // sum the sorted values

    var newTotalValue = slicedDescendingSortedPrizes.reduce(function (accumulator, currentValue) {
      return accumulator.add(currentValue.amount);
    }, BigNumber$1.from(0));
    return _extends({}, drawResults, {
      totalValue: newTotalValue,
      prizes: slicedDescendingSortedPrizes
    });
  } // if not greater than max picks per user, return the whole array


  return drawResults;
}

function filterUndefinedValues(ts) {
  return ts.filter(function (t) {
    return Boolean(t);
  });
}

var debug$3 = /*#__PURE__*/require('debug')('pt:v4-utils-js'); //SOLIDITY SIG: function _findBitMatchesAtIndex(uint256 word1, uint256 word2, uint256 indexOffset, uint8 _bitRangeMaskValue)


function findBitMatchesAtIndex(word1, word2, matchIndex, bitRangeSize) {
  var indexOffset = matchIndex * bitRangeSize;
  debug$3("indexOffset: ", indexOffset);
  var word1DataHexString = word1.toHexString();
  var word2DataHexString = word2.toHexString();
  debug$3(word1DataHexString);
  debug$3(word2DataHexString);
  var bitRangeMaxInt = Math.pow(2, bitRangeSize) - 1; // debug(`Max int: `, bitRangeMaxInt.toString(16))

  var mask = BigInt(bitRangeMaxInt) << BigInt(indexOffset.toString());
  debug$3(mask.toString(16));
  var bits1 = BigInt(word1DataHexString) & BigInt(mask.toString());
  debug$3("bits1: ", bits1.toString(16));
  var bits2 = BigInt(word2DataHexString) & BigInt(mask.toString());
  debug$3("bits2: ", bits2.toString(16));
  var match = bits1 === bits2;
  debug$3("DrawCalculator:: matching " + bits1.toString() + " with " + bits2.toString() + ": " + match);
  return match;
}

function hashUserAddress(address) {
  return keccak256(['address'], [address]);
}

function isBitRangeSizeValid(bitRangeSize, matchCardinality) {
  return bitRangeSize <= Math.floor(256 / matchCardinality);
}

function sumTwoBigNumbers(bn1, bn2) {
  return BigNumber$1.from(bn1).add(BigNumber$1.from(bn2));
}
function sumBigNumbers(numbers) {
  return numbers.reduce(sumTwoBigNumbers, BigNumber$1.from(0));
}

function isTiersValid(tiers) {
  var total = sumBigNumbers(tiers); // @dev The tier total percentage must be less than 1e9
  // @dev Tiers are denominated in 1e9 and represented as a percentage (e.g. 0.1 = 10%).

  return !BigNumber$1.from(total).gt(1000000000);
}

function sanityCheckPrizeDistribution(prizeDistribution) {
  var validBitRangeSize = isBitRangeSizeValid(prizeDistribution.bitRangeSize, prizeDistribution.matchCardinality);
  if (!validBitRangeSize) return 'DrawCalc/bitRangeSize-too-large';
  var validTiers = isTiersValid(prizeDistribution.tiers);
  if (!validTiers) return 'DrawCalc/tiers-gt-100%';
  return '';
}

var sortByBigNumberAsc = function sortByBigNumberAsc(a, b) {
  var aSubB = a.sub(b);
  if (aSubB.isZero()) return 0;
  if (aSubB.isNegative()) return -1;
  return 1;
};
var sortByBigNumberDesc = function sortByBigNumberDesc(a, b) {
  var bSubA = b.sub(a);
  if (bSubA.isZero()) return 0;
  if (bSubA.isNegative()) return -1;
  return 1;
};

var formatTierPercentage = function formatTierPercentage(tier) {
  return parseUnits(tier, 9).toNumber();
};

var parseTierPercentage = function parseTierPercentage(tier) {
  return parseUnits(tier, 9);
};

function updateDrawResultsWithWinningPicks(pickPrizes, results, picks) {
  var _result = results;
  pickPrizes.forEach(function (pickPrize, index) {
    var pick = picks[index];
    if (pickPrize.amount.eq(0)) return;

    var prizeAwardable = _extends({}, pickPrize, {
      pick: BigNumber$1.from(pick.index)
    });

    _result.totalValue = _result.totalValue.add(prizeAwardable.amount);

    _result.prizes.push(prizeAwardable);
  });
  return _result;
}



var index = {
    __proto__: null,
    createDrawResultsObject: createDrawResultsObject,
    filterResultsByValue: filterResultsByValue,
    filterUndefinedValues: filterUndefinedValues,
    findBitMatchesAtIndex: findBitMatchesAtIndex,
    hashUserAddress: hashUserAddress,
    isBitRangeSizeValid: isBitRangeSizeValid,
    isTiersValid: isTiersValid,
    sanityCheckPrizeDistribution: sanityCheckPrizeDistribution,
    sumBigNumbers: sumBigNumbers,
    sumTwoBigNumbers: sumTwoBigNumbers,
    sortByBigNumberDesc: sortByBigNumberDesc,
    sortByBigNumberAsc: sortByBigNumberAsc,
    sortByPrizeAwardableDesc: sortByPrizeAwardableDesc,
    formatTierPercentage: formatTierPercentage,
    parseTierPercentage: parseTierPercentage,
    updateDrawResultsWithWinningPicks: updateDrawResultsWithWinningPicks
};

function calculateNumberOfMatches(pickNumber, winningRandomNumber, matchCardinality, bitRangeSize) {
  var numberOfMatches = 0;

  var _pickNumber = BigNumber$1.from(pickNumber);

  var _winningRandomNumber = BigNumber$1.from(winningRandomNumber);

  for (var matchIndex = 0; matchIndex < matchCardinality; matchIndex++) {
    if (!findBitMatchesAtIndex(_pickNumber, _winningRandomNumber, matchIndex, bitRangeSize)) {
      break;
    }

    numberOfMatches++;
  }

  return numberOfMatches;
}

function calculateNumberOfPrizesForTierIndex(bitRangeSize, tierIndex) {
  if (tierIndex > 0) {
    return (1 << bitRangeSize * tierIndex) - (1 << bitRangeSize * (tierIndex - 1));
  } else {
    return 1;
  }
}

/**
 * Calculates the random number for a pick for a user at a given index.
 * @param address
 * @param pickIndex
 * @returns
 */

function calculatePick(address, pickIndex) {
  var _pickIndex = BigNumber$1.from(pickIndex);

  var abiEncodedValue = pack(['bytes32', 'uint256'], [address, _pickIndex]);
  var userRandomNumber = keccak256(['address'], [abiEncodedValue]);
  return {
    index: _pickIndex.toNumber(),
    hash: userRandomNumber
  };
}

function calculateNumberOfPrizesPerTier(prizeTier) {
  return prizeTier.tiers.map(function (tier, index) {
    return !!tier ? calculateNumberOfPrizesForTierIndex(prizeTier.bitRangeSize, index) : 0;
  });
}

/**
 * Calculates the picks for a user at the give indicies
 * @param address
 * @param pickIndices
 * @returns
 */

function calculatePicks(address, pickIndices) {
  return pickIndices.map(function (pickIndex) {
    return calculatePick(address, pickIndex);
  });
}

var debug$4 = /*#__PURE__*/require('debug')('pt:v4-utils-js:calculatePicksFromAverageTotalSuppliesBetween');
/**
 * Splits picks based on the total supply of tickets compared to other total supplies.
 * @param totalPicks
 * @param ticketPrimaryTotalSupply
 * @param otherTicketsTotalSupply
 * @returns
 */


function calculatePicksFromAverageTotalSuppliesBetween(totalPicks, ticketPrimaryTotalSupply, otherTicketsTotalSupply) {
  if (!BigNumber$1.isBigNumber(ticketPrimaryTotalSupply) || !BigNumber$1.isBigNumber(otherTicketsTotalSupply)) return undefined;
  var numberOfPicks;

  if (ticketPrimaryTotalSupply.gt('0')) {
    numberOfPicks = ticketPrimaryTotalSupply.mul(totalPicks).div(otherTicketsTotalSupply.add(ticketPrimaryTotalSupply));
  } else {
    numberOfPicks = BigNumber$1.from('0');
  }

  debug$4("numberOfPicks " + Math.floor(numberOfPicks.toNumber()));
  return Math.floor(numberOfPicks.toNumber());
}

function calculatePrizeForTierPercentage(tierIndex, tierValue, bitRangeSize, prizeAmount) {
  var numberOfPrizes = calculateNumberOfPrizesForTierIndex(bitRangeSize, tierIndex);
  var fractionOfPrize = calculateFractionOfPrize(numberOfPrizes, tierValue);
  var expectedPrizeAmount = prizeAmount.mul(fractionOfPrize);
  expectedPrizeAmount = expectedPrizeAmount.div(BigNumber$1.from('1000000000000000000'));
  return expectedPrizeAmount;
}

function calculateTierPercentageForPrize(tierIndex, tierPrize, bitRangeSize, prizeAmount) {
  var numberOfPrizes = calculateNumberOfPrizesForTierIndex(bitRangeSize, tierIndex);
  var totalTierPrizes = parseUnits(tierPrize ? tierPrize.toString() : '0', 9).mul(numberOfPrizes);
  var tierPercentage = totalTierPrizes.div(prizeAmount);
  return tierPercentage;
}

function calculateTierIndexFromMatches(matchCardinality, numberOfMatches) {
  if (numberOfMatches > matchCardinality) throw new Error('numberOfMatches cannot be greater than matchCardinality');
  return matchCardinality - numberOfMatches;
}

function calculateUserBalanceFromAccount(accountEntry, drawStartTime, drawEndTime) {
  // beforeOrAtDrawStartTime: S
  // beforeOrAtDrawEndTime: E
  // [], [S], [SE], [E]
  // if beforeOrAtDrawStartTime exists, beforeOrAtDrawEndTime must exist
  // if beforeOrAtDrawEndTime == null, beforeOrAtDrawStartTime must be null
  var sTwab = accountEntry.beforeOrAtDrawStartTime[0];
  var eTwab = accountEntry.beforeOrAtDrawEndTime[0];

  if (!sTwab && !eTwab) {
    return undefined;
  }

  var drawStartTwab;
  var drawEndTwab;

  if (sTwab) {
    drawStartTwab = {
      amount: BigNumber$1.from(sTwab.amount).add(BigNumber$1.from(sTwab.delegateBalance).mul(drawStartTime - sTwab.timestamp)),
      timestamp: drawStartTime
    };
  } else {
    drawStartTwab = {
      amount: BigNumber$1.from(0),
      timestamp: drawStartTime
    };
  }

  drawEndTwab = {
    amount: BigNumber$1.from(eTwab.amount).add(BigNumber$1.from(eTwab.delegateBalance).mul(drawEndTime - eTwab.timestamp)),
    timestamp: drawEndTime
  };
  var average = drawEndTwab.amount.sub(drawStartTwab.amount).div(drawEndTime - drawStartTime);
  return average;
}



var index$1 = {
    __proto__: null,
    calculateCardinality: calculateCardinality,
    calculateFractionOfPrize: calculateFractionOfPrize,
    calculateNormalizedBalance: calculateNormalizedBalance,
    calculatePrizePoolPicksWithDpr: calculatePrizePoolPicksWithDpr,
    calculateNormalizedBalancePicksFromTotalPicks: calculateNormalizeBalancePicksFromTotalPicks,
    calculateNormalizedUserBalancesFromTotalSupply: calculateNormalizedUserBalancesFromTotalSupply,
    calculateNumberOfMatches: calculateNumberOfMatches,
    calculateNumberOfPrizesForTierIndex: calculateNumberOfPrizesForTierIndex,
    calculatePick: calculatePick,
    calculateTotalSupplyOfPicks: calculateTotalSupplyOfPicks,
    calculateNumberOfPrizesPerTier: calculateNumberOfPrizesPerTier,
    calculatePicks: calculatePicks,
    calculatePicksFromAverageTotalSuppliesBetween: calculatePicksFromAverageTotalSuppliesBetween,
    calculatePrizeForTierPercentage: calculatePrizeForTierPercentage,
    calculateTierPercentageForPrize: calculateTierPercentageForPrize,
    calculateTierIndexFromMatches: calculateTierIndexFromMatches,
    calculateUserBalanceFromAccount: calculateUserBalanceFromAccount
};

var MAXIUMUM_TIERS_LENGTH = 16;

function computePrizeAmount(tierIndex, tierValue, bitRangeSize, prizeAmount) {
  if (tierIndex > MAXIUMUM_TIERS_LENGTH) {
    return {
      amount: BigNumber$1.from(0),
      tierIndex: -1
    };
  }

  var expectedPrizeAmount = calculatePrizeForTierPercentage(tierIndex, tierValue, bitRangeSize, prizeAmount);
  return {
    amount: expectedPrizeAmount,
    tierIndex: tierIndex
  };
}

function computePickPrize(pickHash, winningRandomNumber, bitRangeSize, matchCardinality, prize, tiers) {
  var numberOfMatches = calculateNumberOfMatches(pickHash, winningRandomNumber, matchCardinality, bitRangeSize);
  var tierIndex = calculateTierIndexFromMatches(matchCardinality, numberOfMatches);
  var pickAmount = computePrizeAmount(tierIndex, tiers[tierIndex], bitRangeSize, prize);
  return pickAmount;
}

function computePicksPrizes(picks, winningRandomNumber, bitRangeSize, matchCardinality, prize, tiers) {
  return picks.map(function (pick) {
    return computePickPrize(pick.hash, winningRandomNumber, bitRangeSize, matchCardinality, prize, tiers);
  });
}

function computeDrawResults(draw, picks, bitRangeSize, matchCardinality, prize, tiers) {
  var pickPrizes = computePicksPrizes(picks, draw.winningRandomNumber, bitRangeSize, matchCardinality, prize, tiers);
  return updateDrawResultsWithWinningPicks(pickPrizes, createDrawResultsObject(draw.drawId), picks);
}

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var runtime_1 = createCommonjsModule(function (module) {
/**
 * Copyright (c) 2014-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var runtime = (function (exports) {

  var Op = Object.prototype;
  var hasOwn = Op.hasOwnProperty;
  var undefined$1; // More compressible than void 0.
  var $Symbol = typeof Symbol === "function" ? Symbol : {};
  var iteratorSymbol = $Symbol.iterator || "@@iterator";
  var asyncIteratorSymbol = $Symbol.asyncIterator || "@@asyncIterator";
  var toStringTagSymbol = $Symbol.toStringTag || "@@toStringTag";

  function define(obj, key, value) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
    return obj[key];
  }
  try {
    // IE 8 has a broken Object.defineProperty that only works on DOM objects.
    define({}, "");
  } catch (err) {
    define = function(obj, key, value) {
      return obj[key] = value;
    };
  }

  function wrap(innerFn, outerFn, self, tryLocsList) {
    // If outerFn provided and outerFn.prototype is a Generator, then outerFn.prototype instanceof Generator.
    var protoGenerator = outerFn && outerFn.prototype instanceof Generator ? outerFn : Generator;
    var generator = Object.create(protoGenerator.prototype);
    var context = new Context(tryLocsList || []);

    // The ._invoke method unifies the implementations of the .next,
    // .throw, and .return methods.
    generator._invoke = makeInvokeMethod(innerFn, self, context);

    return generator;
  }
  exports.wrap = wrap;

  // Try/catch helper to minimize deoptimizations. Returns a completion
  // record like context.tryEntries[i].completion. This interface could
  // have been (and was previously) designed to take a closure to be
  // invoked without arguments, but in all the cases we care about we
  // already have an existing method we want to call, so there's no need
  // to create a new function object. We can even get away with assuming
  // the method takes exactly one argument, since that happens to be true
  // in every case, so we don't have to touch the arguments object. The
  // only additional allocation required is the completion record, which
  // has a stable shape and so hopefully should be cheap to allocate.
  function tryCatch(fn, obj, arg) {
    try {
      return { type: "normal", arg: fn.call(obj, arg) };
    } catch (err) {
      return { type: "throw", arg: err };
    }
  }

  var GenStateSuspendedStart = "suspendedStart";
  var GenStateSuspendedYield = "suspendedYield";
  var GenStateExecuting = "executing";
  var GenStateCompleted = "completed";

  // Returning this object from the innerFn has the same effect as
  // breaking out of the dispatch switch statement.
  var ContinueSentinel = {};

  // Dummy constructor functions that we use as the .constructor and
  // .constructor.prototype properties for functions that return Generator
  // objects. For full spec compliance, you may wish to configure your
  // minifier not to mangle the names of these two functions.
  function Generator() {}
  function GeneratorFunction() {}
  function GeneratorFunctionPrototype() {}

  // This is a polyfill for %IteratorPrototype% for environments that
  // don't natively support it.
  var IteratorPrototype = {};
  define(IteratorPrototype, iteratorSymbol, function () {
    return this;
  });

  var getProto = Object.getPrototypeOf;
  var NativeIteratorPrototype = getProto && getProto(getProto(values([])));
  if (NativeIteratorPrototype &&
      NativeIteratorPrototype !== Op &&
      hasOwn.call(NativeIteratorPrototype, iteratorSymbol)) {
    // This environment has a native %IteratorPrototype%; use it instead
    // of the polyfill.
    IteratorPrototype = NativeIteratorPrototype;
  }

  var Gp = GeneratorFunctionPrototype.prototype =
    Generator.prototype = Object.create(IteratorPrototype);
  GeneratorFunction.prototype = GeneratorFunctionPrototype;
  define(Gp, "constructor", GeneratorFunctionPrototype);
  define(GeneratorFunctionPrototype, "constructor", GeneratorFunction);
  GeneratorFunction.displayName = define(
    GeneratorFunctionPrototype,
    toStringTagSymbol,
    "GeneratorFunction"
  );

  // Helper for defining the .next, .throw, and .return methods of the
  // Iterator interface in terms of a single ._invoke method.
  function defineIteratorMethods(prototype) {
    ["next", "throw", "return"].forEach(function(method) {
      define(prototype, method, function(arg) {
        return this._invoke(method, arg);
      });
    });
  }

  exports.isGeneratorFunction = function(genFun) {
    var ctor = typeof genFun === "function" && genFun.constructor;
    return ctor
      ? ctor === GeneratorFunction ||
        // For the native GeneratorFunction constructor, the best we can
        // do is to check its .name property.
        (ctor.displayName || ctor.name) === "GeneratorFunction"
      : false;
  };

  exports.mark = function(genFun) {
    if (Object.setPrototypeOf) {
      Object.setPrototypeOf(genFun, GeneratorFunctionPrototype);
    } else {
      genFun.__proto__ = GeneratorFunctionPrototype;
      define(genFun, toStringTagSymbol, "GeneratorFunction");
    }
    genFun.prototype = Object.create(Gp);
    return genFun;
  };

  // Within the body of any async function, `await x` is transformed to
  // `yield regeneratorRuntime.awrap(x)`, so that the runtime can test
  // `hasOwn.call(value, "__await")` to determine if the yielded value is
  // meant to be awaited.
  exports.awrap = function(arg) {
    return { __await: arg };
  };

  function AsyncIterator(generator, PromiseImpl) {
    function invoke(method, arg, resolve, reject) {
      var record = tryCatch(generator[method], generator, arg);
      if (record.type === "throw") {
        reject(record.arg);
      } else {
        var result = record.arg;
        var value = result.value;
        if (value &&
            typeof value === "object" &&
            hasOwn.call(value, "__await")) {
          return PromiseImpl.resolve(value.__await).then(function(value) {
            invoke("next", value, resolve, reject);
          }, function(err) {
            invoke("throw", err, resolve, reject);
          });
        }

        return PromiseImpl.resolve(value).then(function(unwrapped) {
          // When a yielded Promise is resolved, its final value becomes
          // the .value of the Promise<{value,done}> result for the
          // current iteration.
          result.value = unwrapped;
          resolve(result);
        }, function(error) {
          // If a rejected Promise was yielded, throw the rejection back
          // into the async generator function so it can be handled there.
          return invoke("throw", error, resolve, reject);
        });
      }
    }

    var previousPromise;

    function enqueue(method, arg) {
      function callInvokeWithMethodAndArg() {
        return new PromiseImpl(function(resolve, reject) {
          invoke(method, arg, resolve, reject);
        });
      }

      return previousPromise =
        // If enqueue has been called before, then we want to wait until
        // all previous Promises have been resolved before calling invoke,
        // so that results are always delivered in the correct order. If
        // enqueue has not been called before, then it is important to
        // call invoke immediately, without waiting on a callback to fire,
        // so that the async generator function has the opportunity to do
        // any necessary setup in a predictable way. This predictability
        // is why the Promise constructor synchronously invokes its
        // executor callback, and why async functions synchronously
        // execute code before the first await. Since we implement simple
        // async functions in terms of async generators, it is especially
        // important to get this right, even though it requires care.
        previousPromise ? previousPromise.then(
          callInvokeWithMethodAndArg,
          // Avoid propagating failures to Promises returned by later
          // invocations of the iterator.
          callInvokeWithMethodAndArg
        ) : callInvokeWithMethodAndArg();
    }

    // Define the unified helper method that is used to implement .next,
    // .throw, and .return (see defineIteratorMethods).
    this._invoke = enqueue;
  }

  defineIteratorMethods(AsyncIterator.prototype);
  define(AsyncIterator.prototype, asyncIteratorSymbol, function () {
    return this;
  });
  exports.AsyncIterator = AsyncIterator;

  // Note that simple async functions are implemented on top of
  // AsyncIterator objects; they just return a Promise for the value of
  // the final result produced by the iterator.
  exports.async = function(innerFn, outerFn, self, tryLocsList, PromiseImpl) {
    if (PromiseImpl === void 0) PromiseImpl = Promise;

    var iter = new AsyncIterator(
      wrap(innerFn, outerFn, self, tryLocsList),
      PromiseImpl
    );

    return exports.isGeneratorFunction(outerFn)
      ? iter // If outerFn is a generator, return the full iterator.
      : iter.next().then(function(result) {
          return result.done ? result.value : iter.next();
        });
  };

  function makeInvokeMethod(innerFn, self, context) {
    var state = GenStateSuspendedStart;

    return function invoke(method, arg) {
      if (state === GenStateExecuting) {
        throw new Error("Generator is already running");
      }

      if (state === GenStateCompleted) {
        if (method === "throw") {
          throw arg;
        }

        // Be forgiving, per 25.3.3.3.3 of the spec:
        // https://people.mozilla.org/~jorendorff/es6-draft.html#sec-generatorresume
        return doneResult();
      }

      context.method = method;
      context.arg = arg;

      while (true) {
        var delegate = context.delegate;
        if (delegate) {
          var delegateResult = maybeInvokeDelegate(delegate, context);
          if (delegateResult) {
            if (delegateResult === ContinueSentinel) continue;
            return delegateResult;
          }
        }

        if (context.method === "next") {
          // Setting context._sent for legacy support of Babel's
          // function.sent implementation.
          context.sent = context._sent = context.arg;

        } else if (context.method === "throw") {
          if (state === GenStateSuspendedStart) {
            state = GenStateCompleted;
            throw context.arg;
          }

          context.dispatchException(context.arg);

        } else if (context.method === "return") {
          context.abrupt("return", context.arg);
        }

        state = GenStateExecuting;

        var record = tryCatch(innerFn, self, context);
        if (record.type === "normal") {
          // If an exception is thrown from innerFn, we leave state ===
          // GenStateExecuting and loop back for another invocation.
          state = context.done
            ? GenStateCompleted
            : GenStateSuspendedYield;

          if (record.arg === ContinueSentinel) {
            continue;
          }

          return {
            value: record.arg,
            done: context.done
          };

        } else if (record.type === "throw") {
          state = GenStateCompleted;
          // Dispatch the exception by looping back around to the
          // context.dispatchException(context.arg) call above.
          context.method = "throw";
          context.arg = record.arg;
        }
      }
    };
  }

  // Call delegate.iterator[context.method](context.arg) and handle the
  // result, either by returning a { value, done } result from the
  // delegate iterator, or by modifying context.method and context.arg,
  // setting context.delegate to null, and returning the ContinueSentinel.
  function maybeInvokeDelegate(delegate, context) {
    var method = delegate.iterator[context.method];
    if (method === undefined$1) {
      // A .throw or .return when the delegate iterator has no .throw
      // method always terminates the yield* loop.
      context.delegate = null;

      if (context.method === "throw") {
        // Note: ["return"] must be used for ES3 parsing compatibility.
        if (delegate.iterator["return"]) {
          // If the delegate iterator has a return method, give it a
          // chance to clean up.
          context.method = "return";
          context.arg = undefined$1;
          maybeInvokeDelegate(delegate, context);

          if (context.method === "throw") {
            // If maybeInvokeDelegate(context) changed context.method from
            // "return" to "throw", let that override the TypeError below.
            return ContinueSentinel;
          }
        }

        context.method = "throw";
        context.arg = new TypeError(
          "The iterator does not provide a 'throw' method");
      }

      return ContinueSentinel;
    }

    var record = tryCatch(method, delegate.iterator, context.arg);

    if (record.type === "throw") {
      context.method = "throw";
      context.arg = record.arg;
      context.delegate = null;
      return ContinueSentinel;
    }

    var info = record.arg;

    if (! info) {
      context.method = "throw";
      context.arg = new TypeError("iterator result is not an object");
      context.delegate = null;
      return ContinueSentinel;
    }

    if (info.done) {
      // Assign the result of the finished delegate to the temporary
      // variable specified by delegate.resultName (see delegateYield).
      context[delegate.resultName] = info.value;

      // Resume execution at the desired location (see delegateYield).
      context.next = delegate.nextLoc;

      // If context.method was "throw" but the delegate handled the
      // exception, let the outer generator proceed normally. If
      // context.method was "next", forget context.arg since it has been
      // "consumed" by the delegate iterator. If context.method was
      // "return", allow the original .return call to continue in the
      // outer generator.
      if (context.method !== "return") {
        context.method = "next";
        context.arg = undefined$1;
      }

    } else {
      // Re-yield the result returned by the delegate method.
      return info;
    }

    // The delegate iterator is finished, so forget it and continue with
    // the outer generator.
    context.delegate = null;
    return ContinueSentinel;
  }

  // Define Generator.prototype.{next,throw,return} in terms of the
  // unified ._invoke helper method.
  defineIteratorMethods(Gp);

  define(Gp, toStringTagSymbol, "Generator");

  // A Generator should always return itself as the iterator object when the
  // @@iterator function is called on it. Some browsers' implementations of the
  // iterator prototype chain incorrectly implement this, causing the Generator
  // object to not be returned from this call. This ensures that doesn't happen.
  // See https://github.com/facebook/regenerator/issues/274 for more details.
  define(Gp, iteratorSymbol, function() {
    return this;
  });

  define(Gp, "toString", function() {
    return "[object Generator]";
  });

  function pushTryEntry(locs) {
    var entry = { tryLoc: locs[0] };

    if (1 in locs) {
      entry.catchLoc = locs[1];
    }

    if (2 in locs) {
      entry.finallyLoc = locs[2];
      entry.afterLoc = locs[3];
    }

    this.tryEntries.push(entry);
  }

  function resetTryEntry(entry) {
    var record = entry.completion || {};
    record.type = "normal";
    delete record.arg;
    entry.completion = record;
  }

  function Context(tryLocsList) {
    // The root entry object (effectively a try statement without a catch
    // or a finally block) gives us a place to store values thrown from
    // locations where there is no enclosing try statement.
    this.tryEntries = [{ tryLoc: "root" }];
    tryLocsList.forEach(pushTryEntry, this);
    this.reset(true);
  }

  exports.keys = function(object) {
    var keys = [];
    for (var key in object) {
      keys.push(key);
    }
    keys.reverse();

    // Rather than returning an object with a next method, we keep
    // things simple and return the next function itself.
    return function next() {
      while (keys.length) {
        var key = keys.pop();
        if (key in object) {
          next.value = key;
          next.done = false;
          return next;
        }
      }

      // To avoid creating an additional object, we just hang the .value
      // and .done properties off the next function object itself. This
      // also ensures that the minifier will not anonymize the function.
      next.done = true;
      return next;
    };
  };

  function values(iterable) {
    if (iterable) {
      var iteratorMethod = iterable[iteratorSymbol];
      if (iteratorMethod) {
        return iteratorMethod.call(iterable);
      }

      if (typeof iterable.next === "function") {
        return iterable;
      }

      if (!isNaN(iterable.length)) {
        var i = -1, next = function next() {
          while (++i < iterable.length) {
            if (hasOwn.call(iterable, i)) {
              next.value = iterable[i];
              next.done = false;
              return next;
            }
          }

          next.value = undefined$1;
          next.done = true;

          return next;
        };

        return next.next = next;
      }
    }

    // Return an iterator with no values.
    return { next: doneResult };
  }
  exports.values = values;

  function doneResult() {
    return { value: undefined$1, done: true };
  }

  Context.prototype = {
    constructor: Context,

    reset: function(skipTempReset) {
      this.prev = 0;
      this.next = 0;
      // Resetting context._sent for legacy support of Babel's
      // function.sent implementation.
      this.sent = this._sent = undefined$1;
      this.done = false;
      this.delegate = null;

      this.method = "next";
      this.arg = undefined$1;

      this.tryEntries.forEach(resetTryEntry);

      if (!skipTempReset) {
        for (var name in this) {
          // Not sure about the optimal order of these conditions:
          if (name.charAt(0) === "t" &&
              hasOwn.call(this, name) &&
              !isNaN(+name.slice(1))) {
            this[name] = undefined$1;
          }
        }
      }
    },

    stop: function() {
      this.done = true;

      var rootEntry = this.tryEntries[0];
      var rootRecord = rootEntry.completion;
      if (rootRecord.type === "throw") {
        throw rootRecord.arg;
      }

      return this.rval;
    },

    dispatchException: function(exception) {
      if (this.done) {
        throw exception;
      }

      var context = this;
      function handle(loc, caught) {
        record.type = "throw";
        record.arg = exception;
        context.next = loc;

        if (caught) {
          // If the dispatched exception was caught by a catch block,
          // then let that catch block handle the exception normally.
          context.method = "next";
          context.arg = undefined$1;
        }

        return !! caught;
      }

      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        var record = entry.completion;

        if (entry.tryLoc === "root") {
          // Exception thrown outside of any try block that could handle
          // it, so set the completion value of the entire function to
          // throw the exception.
          return handle("end");
        }

        if (entry.tryLoc <= this.prev) {
          var hasCatch = hasOwn.call(entry, "catchLoc");
          var hasFinally = hasOwn.call(entry, "finallyLoc");

          if (hasCatch && hasFinally) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            } else if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }

          } else if (hasCatch) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            }

          } else if (hasFinally) {
            if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }

          } else {
            throw new Error("try statement without catch or finally");
          }
        }
      }
    },

    abrupt: function(type, arg) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc <= this.prev &&
            hasOwn.call(entry, "finallyLoc") &&
            this.prev < entry.finallyLoc) {
          var finallyEntry = entry;
          break;
        }
      }

      if (finallyEntry &&
          (type === "break" ||
           type === "continue") &&
          finallyEntry.tryLoc <= arg &&
          arg <= finallyEntry.finallyLoc) {
        // Ignore the finally entry if control is not jumping to a
        // location outside the try/catch block.
        finallyEntry = null;
      }

      var record = finallyEntry ? finallyEntry.completion : {};
      record.type = type;
      record.arg = arg;

      if (finallyEntry) {
        this.method = "next";
        this.next = finallyEntry.finallyLoc;
        return ContinueSentinel;
      }

      return this.complete(record);
    },

    complete: function(record, afterLoc) {
      if (record.type === "throw") {
        throw record.arg;
      }

      if (record.type === "break" ||
          record.type === "continue") {
        this.next = record.arg;
      } else if (record.type === "return") {
        this.rval = this.arg = record.arg;
        this.method = "return";
        this.next = "end";
      } else if (record.type === "normal" && afterLoc) {
        this.next = afterLoc;
      }

      return ContinueSentinel;
    },

    finish: function(finallyLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.finallyLoc === finallyLoc) {
          this.complete(entry.completion, entry.afterLoc);
          resetTryEntry(entry);
          return ContinueSentinel;
        }
      }
    },

    "catch": function(tryLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc === tryLoc) {
          var record = entry.completion;
          if (record.type === "throw") {
            var thrown = record.arg;
            resetTryEntry(entry);
          }
          return thrown;
        }
      }

      // The context.catch method must only be called with a location
      // argument that corresponds to a known catch block.
      throw new Error("illegal catch attempt");
    },

    delegateYield: function(iterable, resultName, nextLoc) {
      this.delegate = {
        iterator: values(iterable),
        resultName: resultName,
        nextLoc: nextLoc
      };

      if (this.method === "next") {
        // Deliberately forget the last sent value so that we don't
        // accidentally pass it on to the delegate.
        this.arg = undefined$1;
      }

      return ContinueSentinel;
    }
  };

  // Regardless of whether this script is executing as a CommonJS module
  // or not, return the runtime object so that we can declare the variable
  // regeneratorRuntime in the outer scope, which allows this module to be
  // injected easily by `bin/regenerator --include-runtime script.js`.
  return exports;

}(
  // If this script is executing as a CommonJS module, use module.exports
  // as the regeneratorRuntime namespace. Otherwise create a new empty
  // object. Either way, the resulting object will be used to initialize
  // the regeneratorRuntime variable at the top of this file.
   module.exports 
));

try {
  regeneratorRuntime = runtime;
} catch (accidentalStrictMode) {
  // This module should not be running in strict mode, so the above
  // assignment should always work unless something is misconfigured. Just
  // in case runtime.js accidentally runs in strict mode, in modern engines
  // we can explicitly access globalThis. In older engines we can escape
  // strict mode using a global Function call. This could conceivably fail
  // if a Content Security Policy forbids using Function, but in that case
  // the proper solution is to fix the accidental strict mode problem. If
  // you've misconfigured your bundler to force strict mode and applied a
  // CSP to forbid Function, and you're not willing to fix either of those
  // problems, please detail your unique predicament in a GitHub issue.
  if (typeof globalThis === "object") {
    globalThis.regeneratorRuntime = runtime;
  } else {
    Function("r", "regeneratorRuntime = r")(runtime);
  }
}
});

var debug$5 = /*#__PURE__*/require('debug')('pt:v4-utils-js:computePrizeDistributionFromTicketAverageTotalSupplies');
/**
 * Computes a full Prize Distribution for a Prize Pool Network using DPR.
 * @param draw
 * @param prizeTier
 * @param prizePoolTotalSupply average total supply for the duration of the draw provided
 * @param minPickCost read from the contract at that time
 * @param decimals decimals used for the Prize Pool ticket token
 * @returns
 */


function computePrizeDistributionWithDpr(_x, _x2, _x3, _x4, _x5) {
  return _computePrizeDistributionWithDpr.apply(this, arguments);
}

function _computePrizeDistributionWithDpr() {
  _computePrizeDistributionWithDpr = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(draw, prizeTier, prizePoolTotalSupply, minPickCost, decimals) {
    var beaconPeriodSeconds, expiryDuration, bitRangeSize, maxPicksPerUser, tiers, prize, dpr, _totalSupply, matchCardinality, numberOfPicks, prizeDistribution;

    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (decimals === void 0) {
              decimals = 0;
            }

            if (!(!draw || !prizeTier || !prizePoolTotalSupply)) {
              _context.next = 3;
              break;
            }

            return _context.abrupt("return", undefined);

          case 3:
            beaconPeriodSeconds = draw.beaconPeriodSeconds;
            expiryDuration = prizeTier.expiryDuration, bitRangeSize = prizeTier.bitRangeSize, maxPicksPerUser = prizeTier.maxPicksPerUser, tiers = prizeTier.tiers, prize = prizeTier.prize, dpr = prizeTier.dpr;
            _totalSupply = BigNumber$1.from(prizePoolTotalSupply);
            matchCardinality = calculateCardinality(bitRangeSize, _totalSupply, decimals);

            if (_totalSupply.gt('0')) {
              numberOfPicks = calculatePrizePoolPicksWithDpr(bitRangeSize, dpr, minPickCost, prize, _totalSupply, decimals);
            }

            prizeDistribution = {
              bitRangeSize: bitRangeSize,
              matchCardinality: matchCardinality,
              tiers: tiers,
              maxPicksPerUser: maxPicksPerUser,
              expiryDuration: expiryDuration,
              numberOfPicks: BigNumber$1.from(numberOfPicks),
              startTimestampOffset: beaconPeriodSeconds,
              endTimestampOffset: prizeTier.endTimestampOffset,
              prize: prize
            };
            debug$5("computePrizeDistribution:prizeDistribution: ", prizeDistribution);
            return _context.abrupt("return", prizeDistribution);

          case 11:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _computePrizeDistributionWithDpr.apply(this, arguments);
}

var debug$6 = /*#__PURE__*/require('debug')('pt:v4-utils-js:computePrizeDistributionFromTicketAverageTotalSupplies');
/**
 * calculatePrizeDistribution on PrizeDistributionFactory
 * @param draw
 * @param prizeTier
 * @param ticketPrimaryAverageTotalSupply
 * @param ticketSecondaryListAverageTotalSupply
 * @param decimals
 * @returns
 */


function computePrizeDistributionFromTicketAverageTotalSupplies(_x, _x2, _x3, _x4, _x5) {
  return _computePrizeDistributionFromTicketAverageTotalSupplies.apply(this, arguments);
}

function _computePrizeDistributionFromTicketAverageTotalSupplies() {
  _computePrizeDistributionFromTicketAverageTotalSupplies = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(draw, prizeTier, ticketPrimaryAverageTotalSupply, ticketSecondaryListAverageTotalSupply, decimals) {
    var beaconPeriodSeconds, expiryDuration, bitRangeSize, maxPicksPerUser, tiers, prize, ticketPrimaryAverageTotalSupplyBigNumber, ticketSecondaryListAverageTotalSupplyBigNumber, totalAverageSupplies, secondaryTotalAverageSupplies, matchCardinality, numberOfPicks, totalPicks, prizeDistribution;
    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (decimals === void 0) {
              decimals = 0;
            }

            if (!(!draw || !prizeTier || !ticketPrimaryAverageTotalSupply || !ticketSecondaryListAverageTotalSupply)) {
              _context.next = 3;
              break;
            }

            return _context.abrupt("return", undefined);

          case 3:
            beaconPeriodSeconds = draw.beaconPeriodSeconds;
            expiryDuration = prizeTier.expiryDuration, bitRangeSize = prizeTier.bitRangeSize, maxPicksPerUser = prizeTier.maxPicksPerUser, tiers = prizeTier.tiers, prize = prizeTier.prize;
            ticketPrimaryAverageTotalSupplyBigNumber = BigNumber$1.from(ticketPrimaryAverageTotalSupply);
            ticketSecondaryListAverageTotalSupplyBigNumber = ticketSecondaryListAverageTotalSupply.map(BigNumber$1.from); // Sums the ALL ticket average total supplies.

            totalAverageSupplies = sumBigNumbers([ticketPrimaryAverageTotalSupplyBigNumber].concat(ticketSecondaryListAverageTotalSupplyBigNumber)); // Sums ALL SECONDARY ticket average total supplies.

            secondaryTotalAverageSupplies = sumBigNumbers(ticketSecondaryListAverageTotalSupplyBigNumber);
            matchCardinality = calculateCardinality(bitRangeSize, BigNumber$1.from(totalAverageSupplies), decimals);
            totalPicks = BigNumber$1.from(BigNumber$1.from(2).pow(bitRangeSize)).pow(matchCardinality);

            if (BigNumber$1.from(totalAverageSupplies).gt('0')) {
              numberOfPicks = calculatePicksFromAverageTotalSuppliesBetween(totalPicks.toNumber(), BigNumber$1.from(ticketPrimaryAverageTotalSupply), BigNumber$1.from(secondaryTotalAverageSupplies));
            }

            prizeDistribution = {
              bitRangeSize: bitRangeSize,
              matchCardinality: matchCardinality,
              tiers: tiers,
              maxPicksPerUser: maxPicksPerUser,
              expiryDuration: expiryDuration,
              numberOfPicks: BigNumber$1.from(numberOfPicks),
              startTimestampOffset: beaconPeriodSeconds,
              endTimestampOffset: prizeTier.endTimestampOffset,
              prize: prize
            };
            debug$6("computePrizeDistribution:prizeDistribution: ", prizeDistribution);
            return _context.abrupt("return", prizeDistribution);

          case 15:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _computePrizeDistributionFromTicketAverageTotalSupplies.apply(this, arguments);
}

function computeUserPicks(totalNumberOfPicks, address, normalizedBalance) {
  var numberOfPicks = calculateNormalizeBalancePicksFromTotalPicks(totalNumberOfPicks, normalizedBalance);
  var usersAddressHashed = hashUserAddress(address);
  var picks = [];
  var numberOfPicksRemaining = numberOfPicks.toNumber();

  for (var pickIndex = 0; pickIndex < numberOfPicksRemaining; pickIndex++) {
    picks.push(calculatePick(usersAddressHashed, pickIndex));
  }

  return picks;
}

function computeUserWinningPicksForRandomNumber(randomNumber, bitRangeSize, matchCardinality, numberOfPicks, prize, tiers, userAddress, userNormalizedBalance, drawId) {
  var _userNormalizedBalance = BigNumber$1.from(userNormalizedBalance);

  var _prize = BigNumber$1.from(prize);

  var _randomNumber = BigNumber$1.from(randomNumber);

  var userPicks = computeUserPicks(numberOfPicks, userAddress, _userNormalizedBalance);
  var pickPrizes = computePicksPrizes(userPicks, _randomNumber, bitRangeSize, matchCardinality, _prize, tiers);
  return updateDrawResultsWithWinningPicks(pickPrizes, createDrawResultsObject(drawId), userPicks);
}

function computeWinningPicks(userAddress, normalizedBalances, draws, prizeDistributions) {
  return draws.map(function (draw, index) {
    return computeUserWinningPicksForRandomNumber(draw.winningRandomNumber, prizeDistributions[index].bitRangeSize, prizeDistributions[index].matchCardinality, prizeDistributions[index].numberOfPicks, prizeDistributions[index].prize, prizeDistributions[index].tiers, userAddress, normalizedBalances[index], draw.drawId);
  });
}



var index$2 = {
    __proto__: null,
    computeDrawResults: computeDrawResults,
    computePickPrize: computePickPrize,
    computePicksPrizes: computePicksPrizes,
    computePrizeAmount: computePrizeAmount,
    computePrizeDistributionWithDpr: computePrizeDistributionWithDpr,
    computePrizeDistributionFromTicketAverageTotalSupplies: computePrizeDistributionFromTicketAverageTotalSupplies,
    computeUserPicks: computeUserPicks,
    computeUserWinningPicksForRandomNumber: computeUserWinningPicksForRandomNumber,
    computeWinningPicks: computeWinningPicks
};

function encodeWinningPicks(userAddress, drawResults) {
  var claim = {
    userAddress: userAddress,
    drawIds: [],
    winningPickIndices: [],
    encodedWinningPickIndices: ''
  };

  if (drawResults.length === 0) {
    return claim;
  }

  drawResults.forEach(function (drawResult) {
    if (drawResult.totalValue.gt(BigNumber$1.from(0))) {
      claim.drawIds.push(drawResult.drawId); // now add the pickIndices data

      var winningPicks = [];

      for (var _iterator = _createForOfIteratorHelperLoose(drawResult.prizes), _step; !(_step = _iterator()).done;) {
        var prizeAwardable = _step.value;
        winningPicks.push(BigNumber$1.from(prizeAwardable.pick));
      }

      claim.winningPickIndices.push(winningPicks);
    }
  });
  claim.winningPickIndices = claim.winningPickIndices.map(function (data) {
    return data.sort(sortByBigNumberAsc);
  });
  claim.encodedWinningPickIndices = defaultAbiCoder.encode(['uint256[][]'], [claim.winningPickIndices]);
  return claim;
}

function winningPicks(userAddress, normalizedBalances, draws, prizeDistributions) {
  return encodeWinningPicks(userAddress, computeWinningPicks(userAddress, normalizedBalances, draws, prizeDistributions));
}

export { index$1 as calculate, calculateCardinality, calculateFractionOfPrize, calculateNormalizedBalance, calculateNormalizeBalancePicksFromTotalPicks as calculateNormalizedBalancePicksFromTotalPicks, calculateNormalizedUserBalancesFromTotalSupply, calculateNumberOfMatches, calculateNumberOfPrizesForTierIndex, calculateNumberOfPrizesPerTier, calculatePick, calculatePicks, calculatePicksFromAverageTotalSuppliesBetween, calculatePrizeForTierPercentage, calculatePrizePoolPicksWithDpr, calculateTierIndexFromMatches, calculateTierPercentageForPrize, calculateTotalSupplyOfPicks, calculateUserBalanceFromAccount, index$2 as compute, computeDrawResults, computePickPrize, computePicksPrizes, computePrizeAmount, computePrizeDistributionFromTicketAverageTotalSupplies, computePrizeDistributionWithDpr, computeUserPicks, computeUserWinningPicksForRandomNumber, computeWinningPicks, createDrawResultsObject, encodeWinningPicks, filterResultsByValue, filterUndefinedValues, findBitMatchesAtIndex, formatTierPercentage, hashUserAddress, isBitRangeSizeValid, isTiersValid, parseTierPercentage, sanityCheckPrizeDistribution, sortByBigNumberAsc, sortByBigNumberDesc, sortByPrizeAwardableDesc, sumBigNumbers, sumTwoBigNumbers, updateDrawResultsWithWinningPicks, index as utils, winningPicks };
//# sourceMappingURL=v4-utils-js.esm.js.map
